'use client'

import { useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import Link from 'next/link'
import {
  Home,
  LayoutGrid,
  Newspaper,
  MessageSquare, // Icon for Chat
  MoreHorizontal, // Icon for More
  Settings,
  LogOut,
  FileText,
  Tags,
  Search,
  Bug,
  Users,
  ShieldCheck,
  X // Import X icon for close button
} from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '@/contexts/AuthContext'
import { useChatStore } from '@/stores/chatStore' // Import Zustand store

// Helper to check active path (simplified for exact match or starting with for main sections)
const isBottomNavActive = (current: string, href: string): boolean => {
  if (href === '/') return current === href;
  // Handle potential nested routes for sections like media-clips
  return current.startsWith(href) && href !== '/';
};

// Type for Bottom Navigation items
type BottomNavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
};

// Type for the items that go into the "More" menu
type MoreMenuItem = {
  name: string;
  href: string;
  icon: React.ElementType;
  checkAccess?: () => boolean; // Optional access check
}

// Change Chat item to a button type
type BottomNavItemWithAction = BottomNavItem & { action?: () => void };

export default function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, userAccess, canAccessPage, signOut } = useAuth();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const { isChatOpen, openChat } = useChatStore(); // Get openChat action

  // Define the primary bottom navigation items
  const bottomNavItems: BottomNavItemWithAction[] = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutGrid },
    { name: 'Media Clips', href: '/media-clips', icon: Newspaper },
    { 
      name: 'Chat', 
      href: '#', 
      icon: MessageSquare, 
      action: () => {
        // Use store action to open chat
        openChat(); 
      }
    },
  ];

  // Define items for the "More" menu
  // Filter these based on access later
  const moreMenuItemsBase: Omit<MoreMenuItem, 'checkAccess'>[] = [
    { name: 'Media Outlets', href: '/media-outlets', icon: FileText },
    { name: 'Brands', href: '/brands', icon: Tags },
    { name: 'Brand Query', href: '/brand-query', icon: Search },
    { name: 'Settings', href: '/settings', icon: Settings },
    // Admin items - conditionally add based on access
    // { name: 'Access Levels', href: '/admin/access-levels', icon: ShieldCheck },
    // { name: 'User Management', href: '/admin/users', icon: Users },
    // Debug item - conditionally add based on access and env
    // { name: 'Debug', href: '/debug', icon: Bug },
  ];

  // Check if user has admin access
  const isAdmin = userAccess?.accessLevel?.name === 'Admin' || canAccessPage('/admin');

  // Dynamically build the "More" menu items based on access
  const getFilteredMoreMenuItems = (): MoreMenuItem[] => {
    const items: MoreMenuItem[] = [
      { name: 'Media Outlets', href: '/media-outlets', icon: FileText, checkAccess: () => canAccessPage('/media-outlets') },
      { name: 'Brands', href: '/brands', icon: Tags, checkAccess: () => canAccessPage('/brands') },
      { name: 'Brand Query', href: '/brand-query', icon: Search, checkAccess: () => canAccessPage('/brand-query') },
      { name: 'Settings', href: '/settings', icon: Settings, checkAccess: () => canAccessPage('/settings') },
    ];

    // Add Admin items if user has admin access
    if (isAdmin || canAccessPage('/admin')) {
        items.push({ name: 'Access Levels', href: '/admin/access-levels', icon: ShieldCheck, checkAccess: () => canAccessPage('/admin/access-levels') });
        items.push({ name: 'User Management', href: '/admin/users', icon: Users, checkAccess: () => canAccessPage('/admin/users') });
    }

    // Add Debug item if in development and user is admin
    const isDev = process.env.NODE_ENV === 'development';
    if (isDev && isAdmin) {
        items.push({ name: 'Debug', href: '/debug', icon: Bug, checkAccess: () => true }); // Already checked env and admin
    }

    return items.filter(item => item.checkAccess ? item.checkAccess() : false); // Filter based on access check
  }

  const filteredMoreMenuItems = getFilteredMoreMenuItems();

  // Direct navigation function - USE router.push for client-side nav
  const navigateTo = (e: React.MouseEvent<HTMLElement>, href: string): void => {
    e.preventDefault();
    
    // Special handling for media-clips page to ensure default filters apply
    if (href === '/media-clips' && pathname !== '/media-clips') {
      // If there are no existing URL parameters, add the default filters
      const url = new URL(href, window.location.origin);
      
      // Add default category filter if not already set
      if (!url.searchParams.has('category')) {
        // Match desktop behavior with all three default categories
        url.searchParams.set('category', 'Charity,Commercial,Sponsorship');
      }
      
      // Remove default date range filter as requested
      
      router.push(url.pathname + url.search);
    } else {
      // Use router.push for other client-side navigation
      router.push(href);
    }
    
    if (isMoreMenuOpen) setIsMoreMenuOpen(false); // Close menu on navigation
  };

  const handleLogout = async () => {
    try {
      // Clear auth state manually (similar to Sidebar)
      if (typeof window !== 'undefined') {
        localStorage.removeItem('rememberMe');
        // ... clear other relevant localStorage/sessionStorage items ...
        localStorage.removeItem('supabase.auth.token');
      }
      await signOut();
      // Still use window.location for logout redirect to ensure full state clear
      window.location.href = '/'; 
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = '/'; // Force redirect home
    } finally {
        setIsMoreMenuOpen(false); // Ensure menu closes
    }
  };

  const toggleMoreMenu = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  return (
    <>
      {/* Overlay for More Menu - Lighter overlay */}
      {isMoreMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md:hidden" // Hide overlay on desktop
          onClick={toggleMoreMenu} // Close menu when clicking overlay
        />
      )}

      {/* More Menu Drawer - Light theme */}
      <div
        className={clsx(
          'fixed bottom-0 left-0 right-0 z-50 transform bg-white p-4 shadow-lg transition-transform duration-300 ease-in-out rounded-t-2xl md:hidden', // Hide drawer on desktop
          isMoreMenuOpen ? 'translate-y-0' : 'translate-y-full' // Slide fully out of view
        )}
        // Remove inline marginBottom style, let transform handle positioning
        // style={{ marginBottom: '60px' }}
      >
        <div className="relative"> {/* Container for positioning close button */}
            <h3 className="mb-4 text-center text-lg font-semibold text-gray-800">More Options</h3>
             {/* Close button for the drawer */}
             <button
                onClick={toggleMoreMenu}
                className="absolute top-0 right-0 -mt-1 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close more menu"
             >
                <X className="h-6 w-6" /> {/* Use X icon */}
             </button>

            <div className="space-y-1">
              {filteredMoreMenuItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => navigateTo(e, item.href)}
                  className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100" // Adjusted padding/colors
                >
                  <item.icon className="h-5 w-5 shrink-0 text-gray-500" /> {/* Consistent icon color */}
                  <span>{item.name}</span>
                </a>
              ))}
               {/* Logout Button in More Menu */}
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-red-600 hover:bg-red-50" // Adjusted padding/colors
              >
                <LogOut className="h-5 w-5 shrink-0 text-red-500 group-hover:text-red-600" />
                <span>Logout</span>
              </button>
            </div>
        </div>
      </div>


      {/* Bottom Navigation Bar - Light theme */}
      {/* Added pb-safe for iOS safe area padding, adding pb-4 for extra spacing */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 flex h-[60px] items-start justify-around border-t border-gray-200 bg-white shadow-md md:hidden pt-1 pb-safe pb-4">
        {bottomNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = isBottomNavActive(pathname, item.href);
          // Check access using canAccessPage, except for Home ('/') which is always allowed
          // Note: Chat button doesn't need explicit access check here as it triggers an action
          const hasAccess = item.href === '/' || item.action || canAccessPage(item.href);

          if (!hasAccess) return null; // Don't render if no access
          
          // Conditionally render Button or Link based on item.action
          if (item.action) {
            // Render as a button if action exists
            return (
              <button
                key={item.name}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    item.action!(); // Call the action (which is now openChat)
                }}
                 // Add active style if chat is open?
                className={clsx(
                  'flex flex-col items-center justify-center px-2 pt-1 pb-0.5 text-xs font-medium transition-colors duration-150 w-[60px] cursor-pointer',
                  isChatOpen ? 'text-[#8A6FE8]' : 'text-gray-500' // Show active color if chat store is open
                )}
              >
                <Icon className={clsx(
                    'h-5 w-5 shrink-0 mb-0.5',
                    isChatOpen ? 'text-[#6366F1]' : 'text-gray-500' // Show active icon color if chat store is open
                )} />
                <span className={clsx(
                    'mt-0 truncate',
                    isChatOpen ? 'text-[#8A6FE8]' : 'text-gray-500' // Show active text color if chat store is open
                 )}>{item.name}</span>
              </button>
            );
          } else {
             // Render as a Link if no action (standard navigation)
             return (
               <Link
                 key={item.name}
                 href={item.href} // Always provide href for Link
                 onClick={(e) => navigateTo(e, item.href)}
                 className={clsx(
                   'flex flex-col items-center justify-center px-2 pt-1 pb-0.5 text-xs font-medium transition-colors duration-150 w-[60px] cursor-pointer',
                   isActive ? 'text-[#8A6FE8]' : 'text-gray-500'
                 )}
               >
                 <Icon className={clsx(
                     'h-5 w-5 shrink-0 mb-0.5',
                     isActive ? 'text-[#6366F1]' : 'text-gray-500'
                 )} />
                 <span className={clsx(
                     'mt-0 truncate',
                     isActive ? 'text-[#8A6FE8]' : 'text-gray-500'
                  )}>{item.name}</span>
               </Link>
             );
          }
        })}

        {/* More Button */}
        <button
          onClick={toggleMoreMenu}
          className={clsx(
            'flex flex-col items-center justify-center px-2 pt-1 pb-0.5 text-xs font-medium transition-colors duration-150 w-[60px]', // Fixed width
             isMoreMenuOpen ? 'text-[#8A6FE8]' : 'text-gray-500' // Match active state style if menu is open
          )}
        >
          <MoreHorizontal className="h-5 w-5 shrink-0 mb-0.5" />
          <span className="mt-0 truncate">More</span>
        </button>
      </nav>
    </>
  );
} 